import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button } from '@crisellevaldez/speculo-ui';
import { LayoutDashboard, Moon, MessageCircle, Sparkles } from 'lucide-react';
// Sparkles used in features list
import { useUser } from '../../hooks/graphql/users/useUser';
import { updateUser } from '../../api/remiApi';
import { useSupportContact } from '../../hooks/useSupportContact';

const CURRENT_INTRO_VERSION = 1;
const LS_KEY = 'new-design-intro-dismissed';

const features = [
  { icon: LayoutDashboard, label: 'Customizable dashboard' },
  { icon: Moon, label: 'Dark mode supported' },
  { icon: MessageCircle, label: 'Remi chat' },
  { icon: Sparkles, label: 'Many more improvements' },
];

export const NewDesignIntroModal: FC = () => {
  const { user, hasLoaded } = useUser();
  const supportContact = useSupportContact();
  const [open, setOpen] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!hasLoaded || !user || dismissedRef.current) return;
    // Check localStorage first (instant, survives reload even if GQL is slow)
    const lsDismissed = Number(localStorage.getItem(LS_KEY) || '0');
    if (lsDismissed >= CURRENT_INTRO_VERSION) return;
    const seen = user.preferences?.uiHints?.newDesignIntroVersion ?? 0;
    if (seen < CURRENT_INTRO_VERSION) {
      setOpen(true);
    }
  }, [hasLoaded, user]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    setOpen(false);
    localStorage.setItem(LS_KEY, String(CURRENT_INTRO_VERSION));
    if (user?.id) {
      updateUser(user.id, {
        preferences: { uiHints: { newDesignIntroVersion: CURRENT_INTRO_VERSION } },
      }).catch(() => {});
    }
  }, [user?.id]);

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={dismiss} size="lg">
      <Modal.Body className="py-6 px-5 sm:py-10 sm:px-8 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Welcome to the new dashboard</h2>
        <p className="text-xs sm:text-sm text-muted-foreground mb-6 sm:mb-8">
          We've been working on some exciting updates.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {features.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 rounded-lg border border-foreground/10 bg-muted/30 p-3 text-left"
            >
              <Icon className="w-5 h-5 text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground">{label}</span>
            </div>
          ))}
        </div>

        <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
          If you have questions, reach out to{' '}
          <a
            href={`mailto:${supportContact.email}`}
            className="text-primary hover:underline font-medium"
          >
            {supportContact.email}
          </a>
        </p>

        <Button
          onClick={dismiss}
          size="lg"
          className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold text-sm sm:text-base md:text-lg py-3 sm:py-4 md:py-5 rounded-md shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
        >
          Got it, let's go!
        </Button>
      </Modal.Body>
    </Modal>
  );
};
